import type { ReactNode } from 'react';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Indicator } from '@components/Notifications/shared';
import { AvatarUI } from '../avatar/Avatar';
import type { Pathname } from '../link/AccessibleLink';
import { AccessibleLink } from '../link/AccessibleLink';

const AVATAR_SIZE = 24;

interface CommonNavLink {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

interface NavLinkButtonProps extends CommonNavLink {
  icon: ReactNode;
}

interface NavLinkProps extends CommonNavLink {
  pathname: Pathname | null;
  icon: ReactNode;
  notificationIndicator?: boolean;
}

interface NavLinkAvatarProps extends CommonNavLink {
  avatarUrl?: string;
  webappUri: Pathname;
  publicAddress: string;
  username: string;
}

export const MobileNavLink = (props: NavLinkProps) => {
  return (
    <AccessibleLink
      className="appearance-[button] no-underline active:scale-[0.98]"
      onClick={e => {
        e.stopPropagation();
        props.onClick?.();
      }}
      pathnameUri={props.pathname || '/'}
    >
      <Container isActive={!!props.isActive} icon={props.icon} className="relative">
        {!!props.notificationIndicator && <Indicator className="-top-0.5 right-[3px]" />}
        <span className="home-label font-base text-base-xs font-medium">{props.label}</span>
      </Container>
    </AccessibleLink>
  );
};

export const MobileNavLinkButton = (props: NavLinkButtonProps) => {
  return (
    <button className="active:scale-[0.98]" onClick={props.onClick}>
      <Container isActive={!!props.isActive} icon={props.icon}>
        <span className="home-label font-base text-base-xs font-medium">{props.label}</span>
      </Container>
    </button>
  );
};

export const MobileNavLinkAvatar = (props: NavLinkAvatarProps) => {
  return (
    <AccessibleLink
      className="appearance-[button] no-underline active:scale-[0.98]"
      pathnameUri={props.webappUri}
      onClick={e => {
        e.stopPropagation();
        props.onClick?.();
      }}
    >
      <Container isActive={!!props.isActive}>
        <AvatarUI
          size={AVATAR_SIZE}
          src={props.avatarUrl}
          walletAddress={props.publicAddress}
          username={props.username}
          disableLink
          borderRadius={24}
          webappUriPath={props.webappUri}
        />
        <span className="home-label font-base text-base-xs font-medium">{props.label}</span>
      </Container>
    </AccessibleLink>
  );
};

const Container = ({
  children,
  className,
  isActive,
  icon,
}: {
  children: ReactNode;
  className?: string;
  isActive: boolean;
  icon?: React.ReactNode;
}) => {
  return (
    <div
      className={twMerge(
        'flex min-h-[43px] flex-col items-center justify-center gap-1 data-[state=active]:text-black data-[state=inactive]:text-neutral400',
        className,
      )}
      data-state={isActive ? 'active' : 'inactive'}
    >
      {!!icon && (
        <div className="flex h-[25px] w-[27px] flex-shrink-0 items-center justify-center [&_svg]:h-5">
          {icon}
        </div>
      )}
      {children}
    </div>
  );
};
